import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  BulkUpdateSystemsInput,
  BulkUpdateSystemsResult,
  CompleteMaintenanceEventResult,
  CustomerSystemsResponse,
  DiscardMaintenanceEventResult,
  ExpiringQuery,
  ExpiringResponse,
  ImportPreview,
  ImportResult,
  MaintenanceAttentionResponse,
  MaintenanceEventListResponse,
  PendingSystemsResponse,
  SystemStage,
} from "@msc/shared";

import { api } from "../lib/api.js";

/**
 * Datenzugriff für den Reiter „Systeme" der Kundenakte (Spec §4–§6).
 * Jede Mutation, die den Systembestand eines Kunden ändert, hängt an
 * `useInvalidateSystems` — die Karte zeigt danach den frischen Stand.
 */

/**
 * Die Formen des Reiters „Systeme" kommen als Zod-Schemata aus `@msc/shared`
 * und werden hier nur weitergereicht — vorher standen sie ein zweites Mal von
 * Hand hier neben `systems.service.ts` (gleiche Begründung wie bei den
 * Übersichtsformen weiter unten, Befund U2). `SoftwareEntry` behält seinen
 * Namen, damit bestehende Importpfade der Komponenten gültig bleiben.
 */
export type {
  CustomerSystem,
  SystemAttributeEntry,
  SystemSoftwareEntry as SoftwareEntry,
} from "@msc/shared";

export interface ProductRef {
  id: string;
  name: string;
  vendor: string | null;
  versionScheme: "nummer" | "version" | "release_und_stand";
  warnOrangeMonths: number | null;
  warnRedMonths: number | null;
  versionCount: number;
  systemCount: number;
  /** true, sobald mindestens eine Version ein eigenes Wartungsende trägt (Aufgabe 12). */
  hasLifecycle: boolean;
}

export interface VersionRef {
  id: string;
  label: string;
  systemCount: number;
  parentVersionId: string | null;
  parentVersionLabel: string | null;
  maintenanceEnd: string | null;
  granularity: "tag" | "quartal" | null;
  source: string | null;
  inheritedFromLine: boolean;
}

function useInvalidateSystems(customerId: string) {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["maintenance", "systems", customerId] });
}

export function useCustomerSystems(customerId: string) {
  return useQuery({
    queryKey: ["maintenance", "systems", customerId],
    queryFn: async () =>
      (await api.get<CustomerSystemsResponse>(`/customers/${customerId}/systems`)).items,
    enabled: Boolean(customerId),
  });
}

export function useCreateSystem(customerId: string) {
  const invalidate = useInvalidateSystems(customerId);
  return useMutation({
    mutationFn: (payload: {
      name: string;
      systemKey?: string;
      stage: SystemStage;
      engagementId?: string;
      proactiveMaintenance: boolean;
      note?: string;
    }) => api.post<{ id: string }>(`/customers/${customerId}/systems`, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateSystem(customerId: string) {
  const invalidate = useInvalidateSystems(customerId);
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      name?: string;
      systemKey?: string;
      stage?: SystemStage;
      engagementId?: string | null;
      proactiveMaintenance?: boolean;
      note?: string;
    }) => api.patch<{ ok: true }>(`/systems/${id}`, payload),
    onSuccess: invalidate,
  });
}

export function useUpsertSoftware(customerId: string) {
  const invalidate = useInvalidateSystems(customerId);
  return useMutation({
    mutationFn: ({
      systemId,
      ...payload
    }: {
      systemId: string;
      productId: string;
      versionId: string;
      asOf: string;
    }) => api.put<{ ok: true }>(`/systems/${systemId}/software`, payload),
    onSuccess: invalidate,
  });
}

/**
 * PUT /systems/:id/attributes — Merkmalswert an einem System setzen oder
 * ändern (Aufgabe 16, Systeme-Reiter). Invalidiert wie `useUpdateSystem` die
 * Systemliste des Kunden, denn die gesetzten Merkmale hängen an `system.attributes`.
 */
export function useSetSystemAttribute(customerId: string) {
  const invalidate = useInvalidateSystems(customerId);
  return useMutation({
    mutationFn: ({
      systemId,
      ...payload
    }: {
      systemId: string;
      defId: string;
      value: string;
    }) => api.put<{ ok: true }>(`/systems/${systemId}/attributes`, payload),
    onSuccess: invalidate,
  });
}

/** Globale Vorlaufwerte-Rückfälle, wie sie greifen, solange ein Produkt keine eigenen trägt (Spec §5). */
export interface MaintenanceThresholdDefaults {
  warnOrangeMonths: number;
  warnRedMonths: number;
}

/**
 * Liefert Produktliste UND die globalen Vorlaufwerte-Rückfälle in einem
 * Aufruf (Review Aufgabe 12, Befund 1). Vorher waren die Rückfallwerte als
 * Konstanten im Katalog-Reiter abgeschrieben — jetzt kommen sie vom selben
 * Endpunkt, den der Reiter ohnehin aufruft (`GET /maintenance/products`), und
 * das Backend bleibt einzige Quelle.
 */
export function useProducts() {
  return useQuery({
    queryKey: ["maintenance", "products"],
    queryFn: () =>
      api.get<{ items: ProductRef[]; defaults: MaintenanceThresholdDefaults }>(
        "/maintenance/products",
      ),
  });
}

export function useVersions(productId: string | null) {
  return useQuery({
    queryKey: ["maintenance", "versions", productId],
    enabled: productId !== null,
    queryFn: async () =>
      (
        await api.get<{ items: VersionRef[] }>(`/maintenance/products/${productId}/versions`)
      ).items,
  });
}

/**
 * Legt bei Bedarf ein neues Produkt an — ausschließlich als Zwischenschritt
 * beim Erfassen eines Softwarestands/Wartungsziels in `SoftwarePicker`
 * (Befund U3). Der eigentliche Datensatz (Softwarestand, Wartungsvorgang)
 * ist zu diesem Zeitpunkt noch NICHT gespeichert — `meta.silent` unterdrückt
 * deshalb den globalen „Gespeichert"-Toast aus `main.tsx`, der sonst
 * fälschlich Erfolg für den noch ungespeicherten Vorgang suggeriert.
 */
export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; vendor?: string }) =>
      api.post<{ id: string; created: boolean }>("/maintenance/products", payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maintenance", "products"] }),
    meta: { silent: true },
  });
}

/** Legt bei Bedarf eine neue Version an — dieselbe Begründung wie bei `useCreateProduct` (Befund U3). */
export function useCreateVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { productId: string; label: string }) =>
      api.post<{ id: string; created: boolean; parentVersionId: string | null }>(
        "/maintenance/versions",
        payload,
      ),
    onSuccess: (_result, variables) =>
      void qc.invalidateQueries({ queryKey: ["maintenance", "versions", variables.productId] }),
    meta: { silent: true },
  });
}

export function useUpsertLifecycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      versionId: string;
      maintenanceEnd: string;
      granularity: "tag" | "quartal";
      source?: string;
    }) => api.put<{ id: string }>("/maintenance/lifecycles", payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maintenance"] }),
  });
}

/**
 * POST /maintenance/products/merge — zwei Katalogeinträge zusammenführen
 * (Aufgabe 15, Spec §8). `from` verschwindet, seine Versionen und Stände
 * ziehen nach `into`. Invalidiert den gesamten `maintenance`-Cache, nicht nur
 * `products`: betroffene Versionslisten und Systemstände hängen vom
 * verschwundenen Produkt ab.
 */
export function useMergeProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { fromId: string; intoId: string }) =>
      api.post<{ movedVersions: number; movedStates: number }>("/maintenance/products/merge", payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maintenance"] }),
  });
}

/**
 * PATCH /maintenance/products/:id — Vorlaufwerte eines Produkts ändern
 * (Katalog-Reiter, Aufgabe 12). `null` löscht den produktspezifischen Wert
 * wieder, die globale Schwelle greift dann (Spec §5).
 */
/**
 * Versionen in ein anderes Produkt verschieben (Katalogpflege).
 *
 * Nachfahren nimmt der Server von selbst dazu, und er lehnt ab statt teilweise
 * auszuführen — die Fehlermeldung nennt die betroffenen Versionen und gehört
 * deshalb unverändert an die Oberfläche durchgereicht.
 */
export function useMoveVersions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      fromProductId: string;
      toProductId: string;
      versionIds: string[];
    }) =>
      api.post<{ movedVersions: number; movedStates: number; movedEventTargets: number }>(
        "/maintenance/versions/move",
        payload,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maintenance"] }),
  });
}

export function useUpdateProductThresholds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      /** Stammdaten — seit dem 2026-08-19 aenderbar (vorher nur die Schwellen). */
      name?: string;
      vendor?: string | null;
      versionScheme?: "nummer" | "version" | "release_und_stand";
      warnOrangeMonths?: number | null;
      warnRedMonths?: number | null;
    }) => api.patch<{ id: string }>(`/maintenance/products/${id}`, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maintenance"] }),
  });
}

/**
 * Datenzugriff für den Merkmale-Reiter der Einstellungen (Aufgabe 12).
 * Anders als der Softwarekatalog entstehen Merkmalsdefinitionen nicht beim
 * Tippen — sie werden hier bewusst verwaltet.
 */
export interface AttributeDefRef {
  id: string;
  name: string;
  allowedValues: string[];
  usageCount: number;
}

export function useAttributeDefs() {
  return useQuery({
    queryKey: ["maintenance", "attribute-defs"],
    queryFn: async () =>
      (await api.get<{ items: AttributeDefRef[] }>("/maintenance/attribute-defs")).items,
  });
}

export function useCreateAttributeDef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; allowedValues: string[] }) =>
      api.post<{ id: string }>("/maintenance/attribute-defs", payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maintenance", "attribute-defs"] }),
  });
}

export function useUpdateAttributeDef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      name?: string;
      allowedValues?: string[];
    }) => api.patch<{ ok: true }>(`/maintenance/attribute-defs/${id}`, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maintenance", "attribute-defs"] }),
  });
}

/**
 * Datenzugriff für Wartungsvorgänge eines Systems (Spec §4, Aufgabe 16): der
 * Systemstand wird nicht separat gepflegt, sondern schreibt sich beim
 * Abschließen eines Vorgangs aus der geleisteten Arbeit fort.
 */
/**
 * Form und Statuswerte kommen als Zod-Schema aus `@msc/shared` (gleiche
 * Begründung wie bei den Übersichtsformen weiter unten, Befund U2) — vorher
 * stand die Form hier ein zweites Mal von Hand neben `events.service.ts`.
 */
export type {
  MaintenanceEvent,
  MaintenanceEventStatus,
  MaintenanceEventTarget,
} from "@msc/shared";

export function useSystemEvents(systemId: string) {
  return useQuery({
    queryKey: ["maintenance", "events", systemId],
    queryFn: async () =>
      (await api.get<MaintenanceEventListResponse>(`/systems/${systemId}/events`)).items,
    enabled: Boolean(systemId),
  });
}

/**
 * POST /maintenance/events — Vorgang planen. Ändert noch keinen Softwarestand
 * (das passiert erst beim Abschließen), deshalb reicht die Invalidierung der
 * Vorgangsliste dieses Systems.
 */
export function useCreateMaintenanceEvent(systemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      title: string;
      plannedFor?: string;
      note?: string;
      targets: { productId: string; versionId: string }[];
    }) => api.post<{ id: string }>("/maintenance/events", { systemId, ...payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maintenance", "events", systemId] }),
  });
}

/**
 * POST /maintenance/events/:id/complete — Vorgang als durchgeführt markieren.
 * Schreibt serverseitig die Softwarestände des Systems fort (E1) — hier
 * müssen deshalb sowohl die Vorgangsliste als auch die Systemliste des
 * Kunden (die die Softwarestände trägt) invalidiert werden, sonst zeigt die
 * Oberfläche nach dem Abschließen weiter den alten Stand.
 *
 * Die Antwort trägt `skipped`: Stände, die der Server bewusst NICHT
 * fortgeschrieben hat, weil sie neuer erfasst sind als das Durchführungsdatum
 * (Nachtrag). Der Aufrufer muss das zeigen — sonst wirkt der Abschluss
 * erfolgreich, während der Bestand unverändert blieb.
 */
export function useCompleteMaintenanceEvent(customerId: string, systemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, executedOn }: { eventId: string; executedOn: string }) =>
      api.post<CompleteMaintenanceEventResult>(`/maintenance/events/${eventId}/complete`, {
        executedOn,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["maintenance", "events", systemId] });
      void qc.invalidateQueries({ queryKey: ["maintenance", "systems", customerId] });
    },
  });
}

/**
 * POST /maintenance/events/:id/discard — Vorgang verwerfen, weil die Wartung
 * nicht stattfindet.
 *
 * Anders als `useCompleteMaintenanceEvent` wird hier NUR die Vorgangsliste
 * invalidiert und nicht die Systemliste: Ein verworfener Vorgang fasst den
 * Softwarestand nicht an, es gibt an der Systemkarte also nichts nachzuladen.
 * Der Unterschied in den Invalidierungen ist damit auch die Probe aufs
 * Exempel — würde hier doch ein Stand geschrieben, fiele es in der Oberfläche
 * als veralteter Wert auf.
 */
export function useDiscardMaintenanceEvent(systemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, reason }: { eventId: string; reason: string }) =>
      api.post<DiscardMaintenanceEventResult>(`/maintenance/events/${eventId}/discard`, {
        reason,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maintenance", "events", systemId] }),
  });
}

/**
 * Datenzugriff für die Seite „Wartung" (Spec §6) — Auslaufübersicht,
 * Verantwortungslücken und Zähler fürs Dashboard-Widget.
 *
 * Die Antwortformen kommen als Zod-Schemata aus `@msc/shared` und werden hier
 * nur weitergereicht — vorher standen sie ein zweites Mal von Hand hier und
 * mussten bei jedem neuen Feld (zuletzt der Service Lead, Befund U2)
 * nachgezogen werden.
 */
export type {
  ExpiringRow,
  MaintenanceSummary,
  ProductGroup,
  UnownedSystemRow,
} from "@msc/shared";

/**
 * Die Parameterform steht als `expiringQuerySchema` in `@msc/shared` und wird
 * hier nur weitergereicht — Front- und Backend lesen dieselbe Definition.
 */
export type UseExpiringOpts = Omit<ExpiringQuery, "withinMonths"> & {
  withinMonths?: number;
};

/**
 * GET /maintenance/expiring — je nach `groupBy` liefert das Backend entweder
 * `items` (flache, nach Datum sortierte Liste) oder `groups` (nach
 * Produkt/Version zusammengefasst, siehe `groupByProduct` im Backend). Beide
 * Felder sind hier optional, weil dieselbe Response-Form beide Fälle abbildet.
 */
export function useExpiring(opts: UseExpiringOpts = {}) {
  const params = new URLSearchParams();
  if (opts.segmentId) params.set("segmentId", opts.segmentId);
  if (opts.withinMonths !== undefined) {
    params.set("withinMonths", String(opts.withinMonths));
  }
  if (opts.groupBy) params.set("groupBy", opts.groupBy);
  if (opts.onlyMine !== undefined) params.set("onlyMine", String(opts.onlyMine));
  // Ampel-Filter serverseitig statt im Browser: Der Server schaltet dafür den
  // Zeitraum ab (siehe `expiringQuerySchema`), sonst zeigte der Sprung von der
  // Dashboard-Kachel eine andere Menge, als die Kachel gezählt hat.
  if (opts.status) params.set("status", opts.status);
  const qs = params.toString();

  return useQuery({
    queryKey: ["maintenance", "expiring", opts],
    queryFn: () =>
      api.get<ExpiringResponse>(`/maintenance/expiring${qs ? `?${qs}` : ""}`),
  });
}

/** GET /maintenance/attention — Systeme ohne Beauftragung und veraltete Stände; nicht segment-gescoped (siehe overview.service.ts). */
export function useAttention() {
  return useQuery({
    queryKey: ["maintenance", "attention"],
    queryFn: () => api.get<MaintenanceAttentionResponse>("/maintenance/attention"),
  });
}

// ── Inbetriebnahme importierter Systeme (Sammelbearbeitung) ─────────────────

/** GET /maintenance/systems/pending — Systeme ohne Beauftragung oder ohne „proaktiv warten". */
export function usePendingSystems() {
  return useQuery({
    queryKey: ["maintenance", "systems", "pending"],
    queryFn: () => api.get<PendingSystemsResponse>("/maintenance/systems/pending"),
  });
}

/**
 * POST /maintenance/systems/bulk. Räumt den gesamten Wartungs-Cache ab: Eine
 * zugeordnete Beauftragung mit „proaktiv" ändert nicht nur diese Liste,
 * sondern auch Auslaufübersicht, Ampel-Kachel und die Systemkarte des Kunden.
 */
export function useBulkUpdateSystems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkUpdateSystemsInput) =>
      api.post<BulkUpdateSystemsResult>("/maintenance/systems/bulk", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["maintenance"] });
      void qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

// ── Excel-Import „Kundenübersicht" ──────────────────────────────────────────
//
// Beide Endpunkte nehmen dieselbe Datei als `multipart/form-data` entgegen und
// unterscheiden sich nur im Pfad: `preview` ist der Trockenlauf, `commit` die
// Übernahme. Es gibt bewusst KEINE serverseitig zwischengehaltene Vorschau
// (E7: die Rohdatei wird nirgends abgelegt) — die Maske hält die gewählte
// Datei deshalb im Browser und schickt sie zweimal.

/**
 * Trockenlauf. Kein `useQuery`, obwohl er nichts schreibt: Der Aufruf hängt an
 * einer vom Anwender gewählten Datei, nicht an einem Schlüssel — es gibt
 * nichts zu cachen und nichts automatisch nachzuladen.
 */
export function useImportPreview() {
  return useMutation({
    mutationFn: ({ file, asOf }: { file: File; asOf?: string }) =>
      api.upload<ImportPreview>(
        "/maintenance/import/preview",
        file,
        asOf ? { asOf } : {},
      ),
  });
}

/** Übernahme. Räumt danach den gesamten Wartungs-Cache ab — der Lauf berührt Systeme, Stände, Katalog und die Übersichten gleichermaßen. */
export function useImportCommit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, asOf }: { file: File; asOf?: string }) =>
      api.upload<ImportResult>(
        "/maintenance/import/commit",
        file,
        asOf ? { asOf } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["maintenance"] });
      void qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}
